import type {MoneyTone} from '@pokpok/astryx';
import type {Order, PaymentSummary} from '../api/types';

/**
 * The money rule, in one function, so no screen has to decide it.
 *
 * > Pandan green appears **if and only if** money has been verified server-side
 * > by ReservePay (foundations §4, D-021).
 *
 * `MoneyTone.verified` is the only path to Pandan in the design system, so this
 * is the only place on the guest surface that may return it. There is
 * deliberately no tone meaning "probably paid": if a screen needs one, the
 * answer is no.
 *
 * Consequences a screen agent must not work around:
 *
 *   * A menu price, a cart estimate, a running item price — `neutral`. Never
 *     green, and per 03 §Q3 explicitly so.
 *   * A cart total, an unpaid balance, an order in flight — `pending`.
 *   * "Ready" on Q14 is a prep state, not money. It is not green either, and it
 *     does not come through this function at all.
 */

/**
 * The tone for the total on an order.
 *
 * `settled` and `completed` mean the balance reached zero and the server derived
 * that from payment records — a device may never assert it. Anything else owes
 * money or is in flight, and both are `pending`.
 */
export function orderTotalTone(order: Order | null | undefined): MoneyTone {
  if (!order) return 'neutral';
  if (order.state === 'refunded') return 'refunded';
  if (order.state === 'settled' || order.state === 'completed') return 'verified';
  return 'pending';
}

/** The tone for one payment. Only `succeeded` is verified money. */
export function paymentTone(payment: PaymentSummary): MoneyTone {
  switch (payment.state) {
    case 'succeeded':
      return 'verified';
    case 'refunded':
    case 'partially_refunded':
      return 'refunded';
    default:
      return 'pending';
  }
}

/** True when the order is fully paid, server-side. The only thing that earns Pandan. */
export function isVerifiedPaid(order: Order | null | undefined): boolean {
  return orderTotalTone(order) === 'verified';
}
